
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, LoadingIndicator } from '../../components/ui';
import ReadAloudButton from '../../components/ui/ReadAloudButton';
import ShareToggle from '../../components/ShareToggle';
import { getBaseUrl, logger } from '../../services/utils';
import { useAuth } from '../../contexts/AuthContext';
import { 
  useStory, 
  useStoryActions, 
  useStoryLoading, 
  useStoryError,
  useStoryPages,
  useStoryNavigation
} from '../../contexts/StoryContext';
import StoryCompletionPrompt from '../../components/StoryCompletionPrompt/StoryCompletionPrompt';
import { Header } from '../../components/layout';
import './StoryView.css';

/**
 * StoryView Component
 * Refactored to use StoryContext as the source of truth
 */
const StoryView = () => {
  
  // Router hooks
  const { story_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth context
  const { isAuthenticated, activeChildProfile } = useAuth();
  
  // Story context hooks
  const { story, setStory } = useStory();
  const { isLoading } = useStoryLoading();
  const { error } = useStoryError();
  const { joinStory, leaveStory, loadStory, generatePage, loadStoryForReadAloud, resetStory } = useStoryActions();
  
  // CRITICAL: With the new standardized format, story is already the data directly
  // No need for a separate storyData variable
  // (Removing this line and replacing all instances of storyData with story)
  
  // Local UI state
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  
  // Add a ref to track if user has manually navigated away from the latest page
  const userNavigatedRef = useRef(false);
  // Track the last known page count to detect when new pages are added
  const lastKnownPageCountRef = useRef(0);
  
  // Track if access denied has been handled to prevent multiple redirects
  const accessDeniedHandledRef = useRef(false);
  
  // Track story loading attempts
  const storyLoadingAttemptedRef = useRef(false);
  
  // Handle access denied scenarios
  const handleAccessDenied = useCallback(() => {
    // Prevent duplicate handling
    if (accessDeniedHandledRef.current) return;
    
    console.error('[StoryView] Access denied for story:', story_id);
    accessDeniedHandledRef.current = true;
    
    // Log additional context
    if (story_id && !isNaN(parseInt(story_id, 10))) {
      logger.info(`Access denied for story ${story_id}. This may happen if you're using a different browser or session than when the story was created.`);
    }
    
    // Redirect to homepage immediately - no need for a delay
    navigate('/weaversworld');
  }, [story_id, navigate]);
  
  // Reset component state when story_id changes
  useEffect(() => {
    // Reset all component-local state when story_id changes
    setCurrentPageIndex(0);
    setIsPageTransitioning(false);
    setShowPrompt(false);
    
    // Reset StoryContext to force it to reload
    resetStory();
    
    // Reset refs
    userNavigatedRef.current = false;
    lastKnownPageCountRef.current = 0;
    accessDeniedHandledRef.current = false;
    storyLoadingAttemptedRef.current = false;
    imageTimestampRef.current = {};
    imageRetryCountRef.current = {};
    
    console.log('[StoryView] story_id changed, resetting component state and context');
  }, [story_id, resetStory]);

  // Use StoryContext for loading and managing the story - without cleanup
  // This effect focuses only on loading, without any cleanup function
  useEffect(() => {
    // Skip loading if story ID not provided or access was already denied
    if (!story_id || accessDeniedHandledRef.current) {
      return;
    }
    
    // Prevent duplicate loading attempts
    if (storyLoadingAttemptedRef.current) {
      logger.debug('StoryView', `Skipping story load for story ${story_id} - already attempted`);
      return;
    }
    
    storyLoadingAttemptedRef.current = true;
    
    // Load the story using the StoryContext
    const loadStoryData = async () => {
      try {
        const loadedStory = await loadStory(story_id);
        
        if (!loadedStory) {
          // If loading failed, check if it's due to access denied
          if (error && (error.includes('access') || error.includes('permission'))) {
            handleAccessDenied();
          }
        }
      } catch (err) {
        console.error('[StoryView] Error loading story:', err);
        
        // Check if error is access denied
        if (err.message && (err.message.includes('403') || 
            err.message.includes('access') || 
            err.message.toLowerCase().includes('denied'))) {
          handleAccessDenied();
        }
      }
    };
    
    loadStoryData();
    
    // This effect has no cleanup function at all
  }, [story_id, loadStory, error, handleAccessDenied]);
  
  // Simple WebSocket cleanup effect without StrictMode considerations
  useEffect(() => {
    // Store the leaveStory function itself to avoid closure issues
    const leaveStoryFn = leaveStory;
    const currentStoryId = story_id;
    
    // Return a cleanup function that runs on unmount
    return () => {
      if (currentStoryId && typeof leaveStoryFn === 'function') {
        logger.debug('StoryView', `Leaving story ${currentStoryId} on component unmount`);
        
        try {
          const leavePromise = leaveStoryFn(currentStoryId);
          if (leavePromise && typeof leavePromise.catch === 'function') {
            leavePromise.catch((err) => {
              // Ignore errors during cleanup
              logger.debug('StoryView', `Error in leaveStory promise, ignoring: ${err?.message || 'unknown'}`);
            });
          }
        } catch (e) {
          // Silently handle any errors during cleanup
          logger.debug('StoryView', `Error in cleanup, ignoring: ${e.message}`);
        }
      }
    };
  }, [story_id, leaveStory]); // Only run effect if story_id or leaveStory changes
  
  // Effect to handle auto-navigation and check story completion
  useEffect(() => {
    // Guard against null/undefined story data
    if (!story) return;
    
    // Check if story is completed to show prompt for anonymous users
    const isStoryCompleted = story.pages?.length === story.total_pages;
    if (isStoryCompleted && !isAuthenticated) {
      setShowPrompt(true);
    }
    
    // Get current page count
    const currentPageCount = story.pages?.length || 0;
    const latestPageIndex = currentPageCount - 1;
    
    // Debug log with less verbose output
    console.log(`StoryView effect - story=${story.id} update detected - pages: ${currentPageCount}, current: ${currentPageIndex}`);
    
    // Update the counter regardless - this won't trigger state updates
    const previousPageCount = lastKnownPageCountRef.current || 0;
    const newPagesAdded = currentPageCount > previousPageCount;
    lastKnownPageCountRef.current = currentPageCount;
    
    // Only proceed with auto-navigation logic if all these conditions are met:
    // 1. New pages have been added since our last check
    // 2. We're not already on the latest page
    // 3. We're not in the middle of a page transition animation
    // 4. The user hasn't manually navigated away from the latest page
    if (newPagesAdded && 
        currentPageIndex !== latestPageIndex && 
        !isPageTransitioning && 
        !userNavigatedRef.current) {
      
      const latestPage = story.pages[latestPageIndex];
      
      // Auto-navigate when the latest page has real content
      if (latestPage && latestPage.text && !latestPage.isGenerating) {
        logger.storyView(`Auto-navigating to new page at index ${latestPageIndex}`);
        
        // Use requestAnimationFrame for smoother navigation
        requestAnimationFrame(() => {
          setCurrentPageIndex(latestPageIndex);
          setIsPageTransitioning(true);
          
          // Dispatch event for ReadAloudContext to know about the navigation
          document.dispatchEvent(new CustomEvent('pageNavigation', {
            detail: {
              storyId: story.id,
              pageIndex: latestPageIndex,
              page: latestPage,
              autoNavigated: true
            }
          }));
        });
      }
    }
  // Important: Use only necessary dependencies to prevent unnecessary rerenders
  // The memoized dependencies significantly reduce StoryView rerenders
  // Using story?.pages?.length instead of the whole pages array prevents re-renders when page content changes
  }, [
    story?.id, 
    story?.pages?.length, // Only react to changes in page count, not page content
    currentPageIndex, 
    isAuthenticated, 
    isPageTransitioning
  ]);
  
  // Parse query parameters
  useEffect(() => {
    if (!story || !story.pages) return;
    
    // Get pageIndex from URL if present
    const searchParams = new URLSearchParams(location.search);
    const pageIndexParam = searchParams.get('pageIndex');
    
    if (pageIndexParam !== null) {
      const indexValue = parseInt(pageIndexParam, 10);
      if (!isNaN(indexValue) && indexValue >= 0) {
        // Only update if we have a valid index and the story is loaded
        if (indexValue < story.pages.length) {
          logger.storyView(`Navigating to specific page index from URL: ${indexValue}`);
          // Mark URL navigation as user-initiated if it's not to the latest page
          const isToLatestPage = indexValue === story.pages.length - 1;
          
          if (!isToLatestPage) {
            userNavigatedRef.current = true;
            console.log(`URL navigation to non-latest page ${indexValue} - marking as user navigated`);
          } else {
            userNavigatedRef.current = false;
            console.log(`URL navigation to latest page - not marking as user navigated`);
          }
          
          setCurrentPageIndex(indexValue);
          setIsPageTransitioning(true);
          
          // Dispatch event to notify ReadAloudContext about the manual navigation
          document.dispatchEvent(new CustomEvent('pageNavigation', {
            detail: {
              storyId: story.id,
              pageIndex: indexValue,
              page: story.pages[indexValue],
              userInitiated: true
            }
          }));
        }
      }
    } else {
      // CRITICAL FIX: Only do default navigation if we're not in a user-initiated navigation state
      // This prevents overriding manual navigation with auto-navigation
      if (!userNavigatedRef.current) {
        // If no page index in URL and we've just loaded the story,
        // default to the latest page with text
        const pagesWithText = story.pages.filter(p => p.text);
        if (pagesWithText.length > 0) {
          const latestPageWithText = pagesWithText[pagesWithText.length - 1];
          const latestPageIndex = story.pages.findIndex(p => p.id === latestPageWithText.id);
          
          // Only navigate if we're not already on this page
          if (currentPageIndex !== latestPageIndex) {
            logger.storyView(`Setting default page index to latest page with text: ${latestPageIndex}`);
            
            // This is default navigation to the latest page, not user-initiated navigation away
            console.log(`Default navigation to latest page - not marking as user navigated`);
            
            setCurrentPageIndex(latestPageIndex);
          }
        }
      } else {
        console.log(`Skipping default navigation because user has manually navigated (current page: ${currentPageIndex})`);
      }
    }
  // CRITICAL FIX: Remove currentPageIndex from dependencies to prevent re-running after manual navigation
  }, [location.search, story]);
  
  // Background audio is now managed in App.js
  
  // Generate a new page using StoryContext
  const handleGeneratePage = async () => {
    if (!story_id) return;
    
    // Prevent duplicate calls if story is loading
    if (isLoading) return;
    
    console.log('[DEBUG-GENERATE] Starting page generation', {
      storyId: story_id,
      currentPageCount: story?.pages?.length || 0,
      firstPage: story?.pages?.[0]?.page_number,
      isInitialStory: story?.pages?.length === 0,
      isGeneratingNewPage: story?.pages?.length > 0
    });
    
    // Add a temporary page for existing stories (after first page)
    // to show loading state
    if (story && story.pages && story.pages.length > 0) {
      console.log('[DEBUG-GENERATE] Creating temporary page for existing story');
      
      // Create a temporary page
      const tempPage = {
        id: `temp-${Date.now()}`,
        page_number: story.pages.length,
        text: null,
        image_status: 'pending',
        isGenerating: true // Special flag for temp pages
      };
      
      // Navigate to the temporary page
      const newPageIndex = story.pages.length;
      setCurrentPageIndex(newPageIndex);
      setIsPageTransitioning(true);
    } else {
      console.log('[DEBUG-GENERATE] Initial story page generation - no temporary page created yet');
    }
    
    try {
      // Call the StoryContext's generatePage method
      await generatePage(story_id);
      
      // The actual page update will be handled via StoryContext updates
    } catch (err) {
      console.error('[API] Error generating page:', err);
      
      // Navigate back to the last valid page if there was an error
      if (story && story.pages && story.pages.length > 0) {
        const lastValidIndex = Math.max(0, story.pages.length - 1);
        setTimeout(() => {
          setCurrentPageIndex(lastValidIndex);
        }, 0);
      }
    }
  };
  
  // Navigate to next page
  const goToNext = () => {
    if (story?.pages && currentPageIndex < story.pages.length - 1) {
      const newIndex = currentPageIndex + 1;
      
      // Only mark as navigated away if we're NOT going to the latest page
      // This is critical - we only want to prevent auto-navigation when user
      // explicitly navigates away from the latest page
      if (newIndex < story.pages.length - 1) {
        userNavigatedRef.current = true;
        console.log(`User navigated to middle page ${newIndex} - marking as user navigated`);
      } else {
        // If user is manually navigating TO the last page, we should clear the flag
        userNavigatedRef.current = false;
        console.log(`User navigated to last page - resetting user navigated flag`);
      }
      
      setCurrentPageIndex(newIndex);
      setIsPageTransitioning(true);
      
      // Dispatch an event to notify ReadAloudContext of manual navigation
      document.dispatchEvent(new CustomEvent('pageNavigation', {
        detail: {
          storyId: story.id,
          pageIndex: newIndex,
          page: story.pages[newIndex],
          userInitiated: true
        }
      }));
    }
  };
  
  // Navigate to previous page
  const goToPrevious = () => {
    if (currentPageIndex > 0) {
      const newIndex = currentPageIndex - 1;
      
      // Set the user navigated flag when moving to a previous page,
      // as we're explicitly moving away from the latest content
      userNavigatedRef.current = true;
      console.log(`User navigated to previous page ${newIndex} - marking as user navigated`);
      
      setCurrentPageIndex(newIndex);
      setIsPageTransitioning(true);
      
      // Dispatch an event to notify ReadAloudContext of manual navigation
      document.dispatchEvent(new CustomEvent('pageNavigation', {
        detail: {
          storyId: story.id,
          pageIndex: newIndex,
          page: story.pages[newIndex],
          userInitiated: true
        }
      }));
    }
  };
  
  // Return to home page
  const goToHome = () => {
    // Properly clean up before navigating away
    if (story_id && typeof leaveStory === 'function') {
      leaveStory(story_id);
    }
    
    // Reset story state in context
    // This is critical to prevent state bleeding between stories
    if (typeof resetStory === 'function') {
      resetStory();
    }
    
    // Reset StoryContext completely to ensure it will show loading state when re-entering
    // This prevents the briefly showing previous story content when navigating to a new story
    storyLoadingAttemptedRef.current = false;
    
    navigate('/weaversworld');
  };
  
  // Audio toggle is now managed in App.js
  
  // Read-aloud functionality has been moved to the global ReadAloudContext
  
  // Cache timestamps for images to prevent excessive retries
  const imageTimestampRef = useRef({});
  const imageRetryCountRef = useRef({});
  const [imageRefreshTrigger, setImageRefreshTrigger] = useState(0);
  
  // Force refresh the component when imageRefreshTrigger changes
  // This moved from the renderImage function to follow React hooks rules
  useEffect(() => {
    // This is a no-op but ensures the dependency is tracked for image refreshes
  }, [imageRefreshTrigger]);
  
  // Handle image loading errors with exponential backoff
  const handleImageError = (e, pageId) => {
    console.error('[Image] Failed to load image:', e);
    const retryCount = imageRetryCountRef.current[pageId] || 0;
    
    if (retryCount < 3) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, retryCount) * 1000;
      console.log(`[Image] Retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
      
      setTimeout(() => {
        imageRetryCountRef.current[pageId] = retryCount + 1;
        imageTimestampRef.current[pageId] = Date.now();
        setImageRefreshTrigger(prev => prev + 1);
      }, delay);
    } else {
      console.warn(`[Image] Max retries reached for image ${pageId}`);
      e.target.style.display = 'none';
      e.target.onerror = null;
    }
  };

  // Render image based on status
  const renderImage = (page) => {
    if (!page) return null;
    
    // This hook must be moved outside of this render function to avoid breaking rules of hooks
    // Don't use hooks inside conditional or loop statements
    
    switch (page.image_status) {
      case 'pending':
      case 'processing':
        return <LoadingIndicator message="Drawing..." data-testid="image-loading" />;
      case 'downloading':
        return <LoadingIndicator message="Downloading image..." data-testid="image-loading" />;
      case 'completed':
        // Always use permanent_image_url which is our server's URL for the image
        // Use a stable timestamp from ref to prevent excessive requests
        const timestamp = imageTimestampRef.current[page.id] || 
          (imageTimestampRef.current[page.id] = Date.now());
          
        const imageUrl = page.permanent_image_url ? 
          `${page.permanent_image_url}?t=${timestamp}` : 
          null;
        
        // If we don't have a permanent URL, show a loading indicator
        if (!imageUrl) {
          return <LoadingIndicator message="Preparing image..." data-testid="image-loading" />;
        }
        
        return (
          <div className="story-view__image-container">
            <img 
              src={imageUrl}
              alt="Story illustration" 
              className="story-view__image"
              onError={(e) => handleImageError(e, page.id)}
            />
          </div>
        );
      case 'failed':
        return (
          <div className="story-view__image-error" data-testid="image-error">
            <p>Couldn't draw this yet, will try again soon...</p>
          </div>
        );
      default:
        return null;
    }
  };
  
  // DEBUG: Log what we're getting from the context
  console.log("[StoryView DEBUG]", {
    hasStory: !!story,
    storyType: typeof story,
    storyKeys: story ? Object.keys(story) : []
  });

  // Get current page
  const currentPage = story?.pages?.[currentPageIndex];
  const pageCount = story?.pages?.length || 0;
  
  
  // No full-page loading spinners in this app
  // Instead, we'll render a minimal placeholder that doesn't take the full page
  if (!story) {
    console.log("[StoryView DEBUG] Rendering loading state because story is null");
    return (
      <div className="story-view" data-testid="minimal-loading">
        <div className="story-view__content">
          {error ? (
            <div className="story-view__error" data-testid="error-message">
              <p>{error}{error.includes('access') ? ' - Redirecting to homepage...' : ''}</p>
            </div>
          ) : (
            <div className="story-view__loading-container">
              {console.log('[DEBUG-MESSAGE] Initial loading state message', {
                isLoading,
                hasStory: !!story,
                condition: isLoading && !story ? "Creating your story..." : 
                           isLoading && story ? "Writing next page..." : 
                           "Loading story..."
              })}
              <p>{isLoading && !story ? "Creating your story..." : 
                 isLoading && story ? "Writing next page..." : 
                 "Loading story..."}</p>
              <div className="story-view__progress-indicator">
                <div className="story-view__progress-bar">
                  <div className="story-view__progress-fill"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Error state with no story
  if (error && !story) {
    return (
      <div className="story-view story-view--error">
        <div className="story-view__error" data-testid="error-message">
          <p>{error}</p>
          <Button onClick={goToHome} variant="primary">
            Return Home
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="story-view" 
      data-testid="story-view"
    >
      {/* Header */}
      <header className="story-view__header">
          <div className="story-view__header-content">
            <h1 className="story-view__title">
              {story.title || '[No Title]'}
            </h1>
            <div className="story-view__meta">
              <p className="story-view__theme">Theme: {story.theme}</p>
              <p className="story-view__ending">Ending: {story.desired_ending}</p>
            </div>
          </div>
          
          <div className="story-view__header-controls">
            <div className="story-view__controls-column">
              {/* Share toggle for the current story */}
              <ShareToggle 
                story={story}
                onToggleComplete={(updatedStory) => {
                  console.log('[StoryView] ShareToggle completed with update:', {
                    isPublic: updatedStory?.is_public,
                    hasShareUrl: !!updatedStory?.share_url,
                    hasStoryId: !!updatedStory?.id,
                    id: updatedStory?.id
                  });
                  
                  // We expect the API response to include the ID
                  if (!updatedStory.id) {
                    console.error('[StoryView] Updated story is missing ID', {
                      updatedStoryKeys: Object.keys(updatedStory || {})
                    });
                  }
                  
                  // Pass the response directly to setStory
                  setStory(updatedStory);
                }}
              />
              
              <div className="story-view__read-aloud-header">
                <ReadAloudButton 
                  pageIndex={currentPageIndex}
                  disabled={!currentPage?.text}
                  className="story-view__read-aloud-button"
                />
              </div>
            </div>
          </div>
        </header>

      {/* Error message */}
      {error && (
        <div className="story-view__error" data-testid="error-message">
          <p>{error}{error === 'You do not have access to this story' ? ' - Redirecting to homepage...' : ''}</p>
        </div>
      )}

      {/* Story content */}
      <div className="story-view__content">
        {/* Main content area - conditionally show page content or loading */}
        <div 
          className={`story-view__page ${isPageTransitioning ? 'story-view__page--new' : ''}`}
          data-testid={`story-page-${currentPageIndex + 1}`}
          onAnimationEnd={() => {
            console.log('Animation ended, setting isPageTransitioning to false');
            setIsPageTransitioning(false);
            
            // We should NOT reset userNavigated flag automatically
            // It should only be reset when the user intentionally goes back to the last page,
            // not when the animation ends after auto-navigation
          }}
        >
          {currentPage?.isGenerating ? (
            // Show loading state for a generating page
            <>
              <div className="story-view__loading-container">
                <p>Creating the next page of your story...</p>
                <div className="story-view__progress-indicator">
                  <div className="story-view__progress-bar">
                    <div className="story-view__progress-fill"></div>
                  </div>
                </div>
              </div>
              <div className="story-view__page-number">
                Page {currentPageIndex + 1} of {story.total_pages || pageCount}
              </div>
            </>
          ) : currentPage?.text ? (
            // Show page content if we have text
            <>
              <div className="story-view__text-container">
                <p className="story-view__text">{currentPage.text}</p>
              </div>
              <div className="story-view__image-wrapper">
                {renderImage(currentPage)}
              </div>
              <div className="story-view__page-number">
                Page {currentPageIndex + 1} of {story.total_pages || pageCount}
              </div>
            </>
          ) : (
            // Show loading state if we don't have text content (initial page)
            <div className="story-view__loading-container">
              {console.log('[DEBUG-MESSAGE] Loading message condition triggered', {
                hasCurrentPage: !!currentPage, 
                pageNumber: currentPage?.page_number,
                pageId: currentPage?.id,
                pageCount: pageCount,
                isGenerating: currentPage?.isGenerating,
                hasText: !!currentPage?.text,
                pagesWithText: story?.pages?.filter(p => p.text)?.length || 0
              })}
              <p>{story?.pages?.filter(p => p.text)?.length > 0 ? 
                  "Creating the next page of your story..." : 
                  "Starting your story..."}</p>
              <div className="story-view__progress-indicator">
                <div className="story-view__progress-bar">
                  <div className="story-view__progress-fill"></div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Page navigation */}
        {pageCount > 1 && (
          <div className="story-view__navigation-controls">
            <button
              className="story-view__nav-arrow story-view__nav-arrow--prev"
              onClick={goToPrevious}
              disabled={currentPageIndex <= 0}
              aria-label="Previous page"
            >
              ←
            </button>
            <button
              className="story-view__nav-arrow story-view__nav-arrow--next"
              onClick={goToNext}
              disabled={currentPageIndex >= pageCount - 1}
              aria-label="Next page"
            >
              →
            </button>
          </div>
        )}
      </div>

      {/* Story completion prompt for anonymous users */}
      {showPrompt && !isAuthenticated && story.pages?.length === story.total_pages && (
        <StoryCompletionPrompt onClose={() => setShowPrompt(false)} />
      )}
      
      {/* Action controls */}
      <div className="story-view__controls">
        <div className="story-view__buttons-row">
          {/* Always show Back to Home button */}
          <Button 
            onClick={goToHome}
            variant="secondary"
            data-testid="back-home-button"
          >
            Back to Home
          </Button>

          {/* Toggle between Create Next Page and Create New Story */}
          {story.pages?.length < story.total_pages ? (
            <Button 
              onClick={handleGeneratePage}
              disabled={isLoading}
              data-testid="generate-button"
            >
              {isLoading ? 'Creating...' : 'Create Next Page'}
            </Button>
          ) : (
            <Button 
              onClick={() => {
                // Clean up current story
                if (story_id && typeof leaveStory === 'function') {
                  leaveStory(story_id);
                }
                
                // Reset story state to prevent state bleeding
                if (typeof resetStory === 'function') {
                  resetStory();
                }
                
                // Reset the story loading tracking to ensure fresh load on next story view
                storyLoadingAttemptedRef.current = false;
                
                // Navigate to create new story
                navigate('/weaversworld/create');
              }}
              variant="primary"
              data-testid="new-story-button"
            >
              Create New Story
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(StoryView);
